/**
 * Token persistence behind one async interface.
 *
 * Native builds use Capacitor Preferences (backed by UserDefaults on iOS and
 * SharedPreferences on Android), which survives app restarts and is not
 * cleared when the webview's storage is evicted. Web builds use localStorage.
 *
 * The backend is resolved once, lazily, and the Capacitor plugin is loaded via
 * dynamic import so the web bundle never pulls it in.
 */

const ACCESS_TOKEN_KEY = 'hfb.accessToken';
const REFRESH_TOKEN_KEY = 'hfb.refreshToken';

const isNative = () =>
  typeof window !== 'undefined' &&
  window.Capacitor != null &&
  typeof window.Capacitor.isNativePlatform === 'function' &&
  window.Capacitor.isNativePlatform();

/** True inside the Capacitor native webview (iOS/Android), false on the web. */
export const isNativePlatform = () => isNative();

/** localStorage throws in private-mode Safari and inside some webviews. */
const memoryFallback = new Map();

const webBackend = {
  async get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return memoryFallback.get(key) ?? null;
    }
  },
  async set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      memoryFallback.set(key, value);
    }
  },
  async remove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      memoryFallback.delete(key);
    }
  },
};

let backendPromise = null;

function resolveBackend() {
  if (backendPromise) return backendPromise;

  backendPromise = (async () => {
    if (!isNative()) return webBackend;
    try {
      const { Preferences } = await import('@capacitor/preferences');
      return {
        async get(key) {
          const { value } = await Preferences.get({ key });
          return value ?? null;
        },
        async set(key, value) {
          await Preferences.set({ key, value });
        },
        async remove(key) {
          await Preferences.remove({ key });
        },
      };
    } catch {
      // Plugin not installed in this build — degrade rather than crash.
      return webBackend;
    }
  })();

  return backendPromise;
}

export const storage = {
  async get(key) {
    return (await resolveBackend()).get(key);
  },
  async set(key, value) {
    if (value == null) return storage.remove(key);
    return (await resolveBackend()).set(key, String(value));
  },
  async remove(key) {
    return (await resolveBackend()).remove(key);
  },
};

/**
 * Synchronous mirror of the tokens.
 *
 * The fetch wrapper needs the access token on every request; awaiting an async
 * backend each time would serialise requests behind storage I/O. We hydrate
 * once at boot and keep the cache authoritative from then on, writing through
 * to the durable backend in the background.
 */
let cache = { accessToken: null, refreshToken: null };
let hydrated = false;
let hydrationPromise = null;

export function hydrateTokens() {
  if (hydrationPromise) return hydrationPromise;
  hydrationPromise = (async () => {
    const [accessToken, refreshToken] = await Promise.all([
      storage.get(ACCESS_TOKEN_KEY),
      storage.get(REFRESH_TOKEN_KEY),
    ]);
    cache = { accessToken, refreshToken };
    hydrated = true;
    return cache;
  })();
  return hydrationPromise;
}

export const isHydrated = () => hydrated;

export const getAccessToken = () => cache.accessToken;
export const getRefreshToken = () => cache.refreshToken;
export const hasTokens = () => Boolean(cache.accessToken || cache.refreshToken);

export async function setTokens({ accessToken, refreshToken }) {
  cache = {
    accessToken: accessToken ?? cache.accessToken,
    refreshToken: refreshToken ?? cache.refreshToken,
  };
  await Promise.all([
    accessToken ? storage.set(ACCESS_TOKEN_KEY, accessToken) : Promise.resolve(),
    refreshToken ? storage.set(REFRESH_TOKEN_KEY, refreshToken) : Promise.resolve(),
  ]);
}

export async function clearTokens() {
  cache = { accessToken: null, refreshToken: null };
  await Promise.all([storage.remove(ACCESS_TOKEN_KEY), storage.remove(REFRESH_TOKEN_KEY)]);
}

export { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY };
